import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import dayjs from "dayjs";
import {
  useGetNotificationByIdQuery,
  useRemoveNotificationFilesMutation,
} from "../api/notificationApi";
import { formatDateToYMD } from "../../../core/utils/dateFormatter";
import NotificationModals from "../notificationModals";
import { downloadFile } from "../api/fileUploadApi";
import { showToast } from "../../../core/utils/alert";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";

const NotificationDetail = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const [isDeleting, setIsDeleting] = useState<{ [key: string]: boolean }>({});
  const [removeFiles] = useRemoveNotificationFilesMutation();
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [selectedAttachmentToDelete, setSelectedAttachmentToDelete] = useState<
    string | string[]
  >("");
  const [isDeleteBulk, setIsDeleteBulk] = useState(false);
  const [deleteType, setDeleteType] = useState<"notification" | "attachment">(
    "notification"
  );

  // Add this to check the user role
  const userRole = useSelector(selectUserRole);

  // Fetch notification data with refetch on mount
  const {
    data: notificationData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetNotificationByIdQuery(
    { id: id || "" },
    {
      skip: !id,
    }
  );

  // Get notification from response
  const notification = notificationData?.data?.notification;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not published";
    return dayjs(dateString).format("DD MMM YYYY");
  };

  const getFileType = (filename: string): string => {
    const extension = filename.split(".").pop()?.toLowerCase() || "";

    if (
      ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(extension)
    ) {
      return "image";
    } else if (
      ["mp4", "avi", "mov", "webm", "mkv", "flv"].includes(extension)
    ) {
      return "video";
    } else if (["mp3", "wav", "ogg", "flac", "m4a"].includes(extension)) {
      return "audio";
    } else if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) {
      return "other";
    } else {
      // PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, etc. are all document type
      return "document";
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      // Determine file type based on extension
      const fileType = getFileType(filename);

      console.log(`Downloading file: ${filename} with type: ${fileType}`);

      // Call the download function with the determined file type
      await downloadFile(filename, "notifications", fileType);

      console.log(`File ${filename} downloaded successfully`);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  // Update handleDeleteFile to handle both single and multiple files
  const handleDeleteFile = async (filenames: string | string[]) => {
    if (!id) return;

    // Convert to array if single string
    const filenamesToDelete = Array.isArray(filenames)
      ? filenames
      : [filenames];

    // Set deleting state for all files
    const newDeletingState = filenamesToDelete.reduce((acc, filename) => {
      acc[filename] = true;
      return acc;
    }, {} as { [key: string]: boolean });

    setIsDeleting((prev) => ({ ...prev, ...newDeletingState }));

    try {
      const result = await removeFiles({
        id,
        filenames: filenamesToDelete,
      }).unwrap();

      if (result.success) {
        showToast({
          title: "Success",
          text:
            filenamesToDelete.length > 1
              ? `${filenamesToDelete.length} files removed successfully`
              : "File removed successfully",
          icon: "success",
        });

        // Clear selected files if it was a bulk delete
        if (filenamesToDelete.length > 1) {
          setSelectedFiles([]);
        }

        // Refetch notification data to update the UI
        refetch();
      } else {
        showToast({
          title: "Error",
          text: result.error || "Failed to remove file(s)",
          icon: "error",
        });
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Failed to remove file(s)",
        icon: "error",
      });
      console.error("Error removing file(s):", error);
    } finally {
      // Reset deleting state for all files
      const resetDeletingState = filenamesToDelete.reduce((acc, filename) => {
        acc[filename] = false;
        return acc;
      }, {} as { [key: string]: boolean });

      setIsDeleting((prev) => ({ ...prev, ...resetDeletingState }));
      setIsDeleteBulk(false);
    }
  };

  const handleSelectFile = (filename: string) => {
    setSelectedFiles((prev) => {
      if (prev.includes(filename)) {
        return prev.filter((f) => f !== filename);
      } else {
        return [...prev, filename];
      }
    });
  };

  const handleSelectAll = () => {
    if (notification?.attachments) {
      const validAttachments = notification.attachments.filter(
        (att) => att !== ""
      );
      if (selectedFiles.length === validAttachments.length) {
        setSelectedFiles([]);
      } else {
        setSelectedFiles(validAttachments);
      }
    }
  };

  // Update showDeleteModal function
  const showDeleteModal = (
    type: "notification" | "attachment",
    files?: string | string[]
  ) => {
    setDeleteType(type);
    if (type === "attachment" && files) {
      setSelectedAttachmentToDelete(files);
      setIsDeleteBulk(Array.isArray(files));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading notification details...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !notification) {
    return (
      <div className="page-wrapper">
        <div className="content container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="ti ti-alert-circle text-warning fs-1 mb-3"></i>
                  <h4>Notification Not Found</h4>
                  <p>
                    The notification you're looking for doesn't exist or has
                    been removed.
                  </p>
                  <Link
                    to={routes.notificationList}
                    className="btn btn-primary mt-3"
                  >
                    <i className="ti ti-arrow-left me-2"></i>
                    Back to Notifications List
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content container">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Notification Details</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.notificationList}>Notifications</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Notification Details
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <Link
                  to={routes.notificationList}
                  className="btn btn-outline-primary d-flex align-items-center"
                >
                  <i className="ti ti-arrow-left me-2"></i>
                  Back
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Notification Content */}
            <div className="col-md-8 mb-4">
              <div className="card">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-bell fs-16"></i>
                    </span>
                    <h4 className="text-dark mb-0">Notification Content</h4>
                  </div>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <h5>{notification.title}</h5>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span
                        className={`badge badge-soft-${
                          notification.isPublished ? "success" : "warning"
                        } d-inline-flex align-items-center`}
                      >
                        <i className="ti ti-circle-filled fs-5 me-1"></i>
                        {notification.isPublished ? "Published" : "Draft"}
                      </span>
                      <span className="text-muted">|</span>
                      <span className="text-muted">
                        {formatDate(notification.publishedOn)}
                      </span>
                    </div>
                  </div>

                  {/* Add alert for draft status (not published) */}
                  {!notification.isPublished && (
                    <div className="alert alert-warning mb-3" role="alert">
                      <div className="d-flex">
                        <i className="ti ti-alert-triangle mt-2 me-2"></i>
                        <div>
                          <h6 className="alert-heading mb-1">
                            Draft Notification
                          </h6>
                          <p className="mb-0">
                            This notification is currently in draft mode and has
                            not been published yet. Recipients will not see this
                            notification until it is published.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="notification-content mb-4">
                    {notification.content.split("\n").map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Notification Info */}
            <div className="col-md-4 mb-4">
              <div className="card h-100">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-info-circle fs-16"></i>
                    </span>
                    <h4 className="text-dark mb-0">Notification Info</h4>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Created:</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="mb-0 fw-medium">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Status:</p>
                    </div>
                    <div className="col-sm-8">
                      <span
                        className={`badge badge-soft-${
                          notification.isPublished ? "success" : "warning"
                        } d-inline-flex align-items-center`}
                      >
                        <i className="ti ti-circle-filled fs-5 me-1"></i>
                        {notification.isPublished ? "Published" : "Draft"}
                      </span>
                      {notification.isPublished && (
                        <small className="d-block mt-1">
                          Published on{" "}
                          {dayjs(notification.publishedOn).format(
                            "DD MMM YYYY"
                          )}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Audience:</p>
                    </div>
                    <div className="col-sm-8">
                      {/* Case 1: All Centers and All Students */}
                      {notification.toAllCenters &&
                        notification.toAllStudents && (
                          <span className="badge badge-soft-info me-1">
                            All Centers and All Students
                          </span>
                        )}

                      {/* Case 2: All Centers and selected students */}
                      {notification.toAllCenters &&
                        !notification.toAllStudents && (
                          <div>
                            <span className="badge badge-soft-info me-1 mb-1">
                              All Centers
                            </span>
                            {notification.student_names &&
                            notification.student_names.length > 0 ? (
                              <div className="mt-1">
                                <small className="text-muted d-block mb-1">
                                  Selected Students:
                                </small>
                                <div
                                  className="scrollable-badges"
                                  style={{
                                    maxHeight: "80px",
                                    overflowY: "auto",
                                    padding: "3px",
                                    border: "1px solid #f0f0f0",
                                    borderRadius: "4px",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {notification.student_names.map(
                                    (name, index) => (
                                      <span
                                        key={index}
                                        className="badge badge-soft-primary d-block mb-1"
                                        style={{
                                          padding: "0.25rem 0.4rem",
                                          textAlign: "left",
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {name}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        )}

                      {/* Case 3: All Students and selected centers */}
                      {!notification.toAllCenters &&
                        notification.toAllStudents && (
                          <div>
                            <span className="badge badge-soft-info me-1 mb-1">
                              All Students
                            </span>
                            {notification.center_names &&
                            notification.center_names.length > 0 ? (
                              <div className="mt-1">
                                <small className="text-muted d-block mb-1">
                                  Selected Centers:
                                </small>
                                <div
                                  className="scrollable-badges"
                                  style={{
                                    maxHeight: "80px",
                                    overflowY: "auto",
                                    padding: "3px",
                                    border: "1px solid #f0f0f0",
                                    borderRadius: "4px",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {notification.center_names.map(
                                    (name, index) => (
                                      <span
                                        key={index}
                                        className="badge badge-soft-primary d-block mb-1"
                                        style={{
                                          padding: "0.25rem 0.4rem",
                                          textAlign: "left",
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {name}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        )}

                      {/* Case 4: Selected centers and/or students */}
                      {!notification.toAllCenters &&
                        !notification.toAllStudents && (
                          <>
                            {(!notification.center_names ||
                              notification.center_names.length === 0) &&
                            (!notification.student_names ||
                              notification.student_names.length === 0) ? (
                              <span className="badge badge-soft-warning">
                                No Recipients
                              </span>
                            ) : (
                              <>
                                {notification.center_names &&
                                  notification.center_names.length > 0 && (
                                    <div className="mb-2">
                                      <small className="text-muted d-block mb-1">
                                        Selected Centers:
                                      </small>
                                      <div
                                        className="scrollable-badges"
                                        style={{
                                          maxHeight: "80px",
                                          overflowY: "auto",
                                          padding: "3px",
                                          border: "1px solid #f0f0f0",
                                          borderRadius: "4px",
                                          fontSize: "0.85rem",
                                        }}
                                      >
                                        {notification.center_names.map(
                                          (name, index) => (
                                            <span
                                              key={index}
                                              className="badge badge-soft-primary d-block mb-1"
                                              style={{
                                                padding: "0.25rem 0.4rem",
                                                textAlign: "left",
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              {name}
                                            </span>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                                {notification.student_names &&
                                  notification.student_names.length > 0 && (
                                    <div>
                                      <small className="text-muted d-block mb-1">
                                        Selected Students:
                                      </small>
                                      <div
                                        className="scrollable-badges"
                                        style={{
                                          maxHeight: "80px",
                                          overflowY: "auto",
                                          padding: "3px",
                                          border: "1px solid #f0f0f0",
                                          borderRadius: "4px",
                                          fontSize: "0.85rem",
                                        }}
                                      >
                                        {notification.student_names.map(
                                          (name, index) => (
                                            <span
                                              key={index}
                                              className="badge badge-soft-primary d-block mb-1"
                                              style={{
                                                padding: "0.25rem 0.4rem",
                                                textAlign: "left",
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              {name}
                                            </span>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </>
                            )}
                          </>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Attachments Section */}
            <div className="row">
              <div className="col-md-8 mb-4">
                <div className="card h-100">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-paperclip fs-16"></i>
                      </span>
                      <h4 className="text-dark mb-0">Attachments</h4>
                      {notification.attachments &&
                        notification.attachments.some((att) => att !== "") && (
                          <div className="ms-auto d-flex align-items-center gap-3">
                            <div className="form-check mb-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="selectAllFiles"
                                checked={
                                  selectedFiles.length ===
                                  notification.attachments.filter(
                                    (att) => att !== ""
                                  ).length
                                }
                                onChange={handleSelectAll}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="selectAllFiles"
                              >
                                Select All
                              </label>
                            </div>
                            {selectedFiles.length > 0 && (
                              <button
                                className="btn btn-outline-danger btn-sm"
                                data-bs-toggle="modal"
                                data-bs-target="#delete-modal"
                                onClick={() =>
                                  showDeleteModal("attachment", selectedFiles)
                                }
                                disabled={isBulkDeleting}
                              >
                                {isBulkDeleting ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-1"
                                      role="status"
                                    ></span>
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <i className="ti ti-trash me-1"></i>
                                    Delete Selected ({selectedFiles.length})
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="card-body">
                    {notification.attachments &&
                    notification.attachments.some(
                      (attachment) => attachment !== ""
                    ) ? (
                      <div className="row g-2">
                        {notification.attachments
                          .filter((attachment) => attachment !== "")
                          .map((attachment, idx) => {
                            // Determine file type for icon display
                            const extension =
                              attachment.split(".").pop()?.toLowerCase() || "";
                            let iconClass = "file";
                            let iconColor = "text-secondary";

                            // Determine icon and color based on extension
                            if (["pdf"].includes(extension)) {
                              iconClass = "file-pdf";
                              iconColor = "text-danger";
                            } else if (
                              [
                                "jpg",
                                "jpeg",
                                "png",
                                "gif",
                                "webp",
                                "svg",
                              ].includes(extension)
                            ) {
                              iconClass = "image";
                              iconColor = "text-success";
                            } else if (["doc", "docx"].includes(extension)) {
                              iconClass = "file-word";
                              iconColor = "text-primary";
                            } else if (
                              ["xls", "xlsx", "csv"].includes(extension)
                            ) {
                              iconClass = "file-excel";
                              iconColor = "text-success";
                            } else if (["ppt", "pptx"].includes(extension)) {
                              iconClass = "file-powerpoint";
                              iconColor = "text-warning";
                            } else if (
                              ["zip", "rar", "7z"].includes(extension)
                            ) {
                              iconClass = "file-archive";
                              iconColor = "text-info";
                            } else if (
                              ["mp4", "avi", "mov", "webm"].includes(extension)
                            ) {
                              iconClass = "file-video";
                              iconColor = "text-purple";
                            } else if (
                              ["mp3", "wav", "ogg"].includes(extension)
                            ) {
                              iconClass = "file-audio";
                              iconColor = "text-info";
                            } else if (
                              ["txt", "log", "md"].includes(extension)
                            ) {
                              iconClass = "file-alt";
                              iconColor = "text-secondary";
                            } else {
                              iconClass = "file-circle-question";
                              iconColor = "text-muted";
                            }

                            return (
                              <div className="col-md-6 col-xl-4" key={idx}>
                                <div
                                  className={`card attachment-card border ${
                                    selectedFiles.includes(attachment)
                                      ? "border-primary"
                                      : ""
                                  }`}
                                >
                                  <div className="card-body p-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center">
                                        <div className="form-check me-2">
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedFiles.includes(
                                              attachment
                                            )}
                                            onChange={() =>
                                              handleSelectFile(attachment)
                                            }
                                            id={`file-${idx}`}
                                          />
                                        </div>
                                        <div className="attachment-icon me-2">
                                          <i
                                            className={`fas fa-${iconClass} fa-2x ${iconColor}`}
                                          ></i>
                                        </div>
                                        <div className="attachment-info">
                                          <h6
                                            className="mb-0 text-truncate"
                                            style={{ maxWidth: "120px" }}
                                          >
                                            {attachment}
                                          </h6>
                                          <small className="text-muted">
                                            <a
                                              href="#"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                handleDownload(attachment);
                                              }}
                                              className="text-primary d-flex align-items-center"
                                            >
                                              <i className="ti ti-download me-1"></i>
                                              Download
                                            </a>
                                          </small>
                                        </div>
                                      </div>
                                      {/* Individual delete button */}
                                      <div>
                                        <button
                                          className="btn btn-sm text-danger "
                                          title="Delete Attachment"
                                          data-bs-toggle="modal"
                                          data-bs-target="#delete-modal"
                                          onClick={() =>
                                            showDeleteModal(
                                              "attachment",
                                              attachment
                                            )
                                          }
                                          disabled={
                                            isDeleting[attachment] ||
                                            isBulkDeleting
                                          }
                                        >
                                          {isDeleting[attachment] ? (
                                            <span
                                              className="spinner-border spinner-border-sm"
                                              role="status"
                                            ></span>
                                          ) : (
                                            <i className="ti ti-trash"></i>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center p-4 bg-light-200 rounded">
                        <i className="ti ti-file-off fs-3 text-muted"></i>
                        <p className="mt-2 mb-0">
                          No attachments available for this notification.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions Card - in the same row as attachments */}
              <div className="col-md-4 mb-4">
                <div className="card h-100">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-settings fs-16"></i>
                      </span>
                      <h4 className="text-dark mb-0">Actions</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="d-flex flex-column gap-3">
                      <Link
                        to={
                          userRole === "centeradmin"
                            ? routes.editCenterAdminNotification.replace(
                                ":id",
                                id || ""
                              )
                            : routes.editNotification.replace(":id", id || "")
                        }
                        className="btn btn-outline-primary"
                      >
                        <i className="ti ti-edit me-2"></i> Edit Notification
                      </Link>

                      {!notification.isPublished ? (
                        <button
                          className="btn btn-success"
                          data-bs-toggle="modal"
                          data-bs-target="#publish-modal"
                        >
                          <i className="ti ti-send me-2"></i> Publish Now
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-warning"
                          data-bs-toggle="modal"
                          data-bs-target="#unpublish-modal"
                        >
                          <i className="ti ti-x me-2"></i> Unpublish
                        </button>
                      )}

                      <button
                        className="btn btn-outline-danger"
                        data-bs-toggle="modal"
                        data-bs-target="#delete-modal"
                        onClick={() => showDeleteModal("notification")}
                      >
                        <i className="ti ti-trash me-2"></i> Delete Notification
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NotificationModals
        NotificationId={id}
        onSuccess={() => {
          refetch();
        }}
        selectedAttachment={selectedAttachmentToDelete}
        onDeleteAttachment={handleDeleteFile}
        isDeletingAttachment={
          isDeleteBulk
            ? isBulkDeleting
            : isDeleting[selectedAttachmentToDelete as string]
        }
        isDeleteBulk={isDeleteBulk}
        deleteType={deleteType}
      />
    </>
  );
};

export default NotificationDetail;
